import MenuItem from '@material-ui/core/MenuItem'
import classnames from 'classnames'
import { Box } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import React, { memo, useEffect, useMemo, useState } from 'react'
import { AutocompleteSuggestionList } from 'react-admin'
import type { OpenSolarThemeType } from 'Themes'
import { makeOpenSolarStyles } from 'themes/makeOpenSolarStyles'
import { getMaxHeightForAnchorPx, isPlacementAbove, resolveAutocompletePlacement } from './autocompletePlacement'

const DEFAULT_MAX_ITEMS = 50
const useStyles = makeOpenSolarStyles(
  (theme: OpenSolarThemeType) => ({
    backdrop: {
      position: 'fixed',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    },
    nonClickableItem: {
      cursor: 'default',
    },
    /** Applied only for multi-line / wide BOM·Assist typeahead rows. */
    richMenuItem: {
      whiteSpace: 'normal',
      height: 'auto',
      alignItems: 'flex-start',
      paddingTop: 8,
      paddingBottom: 8,
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
    },
    scrollableList: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      width: '100%',
    },
    actionFooter: {
      flexShrink: 0,
    },
  }),
  { name: 'Basic-Autocomplete-Suggestion-List' }
)

const BasicAutocompleteSuggestionList = <T,>({
  maxItems = DEFAULT_MAX_ITEMS,
  width,
  minWidth,
  inputEl,
  loading,
  options,
  onClose,
  renderer,
  handleSelect,
  dropdownFooter,
  suggestionsContainerProps,
}: {
  maxItems?: number
  width?: number
  /** Override paper/row min width (e.g. BOM typeahead needs room for 2-line details). */
  minWidth?: number
  inputEl: HTMLElement // could have referencing issue to get clientWidth
  loading: boolean
  options: T[]
  onClose(): void
  renderer(record: T): React.ReactNode
  handleSelect(record: T): void
  dropdownFooter?: React.ReactNode
  suggestionsContainerProps?: Record<string, unknown>
}) => {
  const translate = useTranslate()
  const classes = useStyles()
  const requestedPlacement = String(suggestionsContainerProps?.placement ?? 'bottom')
  const isMultiLineSuggestion = Boolean(minWidth) || requestedPlacement === 'auto-start'
  const rowWidth = Math.max(width || 0, minWidth || 0, inputEl?.clientWidth || 0) || undefined
  // Lock placement once per open so results loading cannot flip the panel.
  const [resolvedContainerProps] = useState(() => ({
    ...suggestionsContainerProps,
    placement: resolveAutocompletePlacement(inputEl, requestedPlacement),
  }))
  const opensAbove = isPlacementAbove(String(resolvedContainerProps.placement ?? 'bottom'))
  const constrainPanelHeight = isMultiLineSuggestion

  const paperStyle = useMemo(() => {
    const style: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }
    if (minWidth) style.minWidth = minWidth
    if (constrainPanelHeight) {
      style.maxHeight = getMaxHeightForAnchorPx(inputEl, opensAbove)
    }
    return Object.keys(style).length ? style : undefined
  }, [minWidth, constrainPanelHeight, inputEl, opensAbove])
  // BOM / Assist use auto-start or minWidth — keep shop rows compact.
  const richMenuItemClass = isMultiLineSuggestion || opensAbove ? classes.richMenuItem : undefined

  useEffect(() => {
    const inputRoot = inputEl?.closest?.('.MuiInputBase-root') ?? inputEl

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return
      if (inputRoot?.contains(target)) return
      if (target.closest('[data-typeahead-panel]')) return
      onClose()
    }

    document.addEventListener('pointerdown', handlePointerDown, true)
    return () => document.removeEventListener('pointerdown', handlePointerDown, true)
  }, [inputEl, onClose])

  const renderListItem = () => {
    if (loading) {
      return (
        <MenuItem className={classnames([classes.nonClickableItem, richMenuItemClass])}>
          <span>{translate('Loading search results...')}</span>
        </MenuItem>
      )
    } else if (options.length === 0) {
      return (
        <MenuItem className={classnames([classes.nonClickableItem, richMenuItemClass])}>
          <span>{translate('No results found.')}</span>
        </MenuItem>
      )
    } else {
      return options?.slice(0, maxItems).map((record: T, index: number) => {
        if (record == null) return null
        const handleClicked = () => handleSelect(record)
        const maybeRecord = typeof record === 'object' ? (record as { id?: string | number; code?: string }) : undefined
        const recordKey = maybeRecord?.id ?? maybeRecord?.code
        return (
          <Box key={recordKey ?? index} width="100%" minWidth={rowWidth}>
            <MenuItem className={richMenuItemClass} onClick={handleClicked}>
              {renderer(record)}
            </MenuItem>
          </Box>
        )
      })
    }
  }

  return (
    <>
      <AutocompleteSuggestionList
        isOpen={true}
        menuProps={{}}
        inputEl={inputEl}
        variant="outlined"
        suggestionsContainerProps={resolvedContainerProps}
        PaperStyle={paperStyle}
      >
        <div className={classes.scrollableList}>{renderListItem()}</div>
        <div className={classes.actionFooter}>{dropdownFooter}</div>
      </AutocompleteSuggestionList>
      <div className={classes.backdrop} onClick={onClose}></div>
    </>
  )
}

export default memo(BasicAutocompleteSuggestionList)
